import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tooltip, message } from 'antd';
import { PrinterOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import Notify from '../../util/Notify';

const extractTextFromReactElement = (element) => {
    if (element === null || element === undefined) return '';

    // If it's a primitive value
    if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') {
        return element.toString();
    }

    // If it's an array of elements
    if (Array.isArray(element)) {
        return element.map(extractTextFromReactElement).join(' ').replace(/\s+/g, ' ').trim();
    }

    // If it's a React element
    if (element.props) {
        if (element.props.children !== undefined) {
            return extractTextFromReactElement(element.props.children);
        }
    }

    return '';
};

/**
 * Reusable component for Table Actions (Print & Export)
 * @param {Function} onFetchFullData - Async function that returns the full dataset
 * @param {Array} columns - Table columns configuration
 * @param {string} fileName - Base name for the exported file
 * @param {string} pageName - Name of the page for print title and file name
 * @param {Array} excludeColumns - Array of column titles to exclude from export/print
 */
const TableActions = ({ onFetchFullData, columns, fileName = 'report', pageName = 'Report', excludeColumns = [], orientation = 'landscape' }) => {
    const [loading, setLoading] = useState(false);
    const [fullData, setFullData] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);

    // Filter columns for export and print
    const activeColumns = columns.filter(col => {
        if (!col.title) return false;

        const title = col.title.toString();
        const blacklist = [
            'Actions', 'Action', 'Image', 'Selection', 'History', 'S.No',
            ...excludeColumns
        ];

        if (blacklist.includes(title)) return false;
        if (col.key === 'actions' || col.key === 'image' || col.key === 'history' || col.key === 'sNo') return false;

        return true;
    });

    const getValue = (item, col, isExcel = false, index = 0) => {
        if (typeof col.render === 'function') {
            const rawVal = col.dataIndex ? item[col.dataIndex] : item;
            try {
                const rendered = col.render(rawVal, item, index);
                const textVal = extractTextFromReactElement(rendered);
                if (textVal !== null && textVal !== undefined && textVal.trim() !== "") {
                    return textVal.trim();
                }
            } catch (err) {
                console.error("Error in col.render call during TableActions export:", err);
            }
        }

        let value = "";

        if (col.dataIndex) {
            value = item[col.dataIndex];
        } else if (col.key) {
            value = item[col.key];
        }

        // --- SPECIFIC FALLBACKS FOR COMMON ADMIN COLUMNS ---

        // --- 0. Order Management Columns ---
        if (col.title === 'Order Date' || col.key === 'orderDate') {
            const dateVal = item.createdAt || item.date;
            value = dateVal ? new Date(dateVal).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        }

        if (col.title === 'Seller ID' || col.key === 'sellerId') {
            value = item.shopId?.createdBy?.userId || item.shop?.createdBy?.userId || '—';
        }

        if (col.title === 'Seller / Shop' || col.key === 'sellerName') {
            const shop = item.shopId || item.shop;
            const seller = shop?.createdBy || shop?.seller || {};
            const name = [seller.firstName, seller.lastName].filter(Boolean).join(" ");
            value = name ? `${name} / ${shop?.name || ''}` : (shop?.name || '—');
        }

        if (col.title === 'Product Name' || col.key === 'productName') {
            value = item.name || item.item?.name || item.productName || '—';
        }

        if (col.title === 'Product ID' || col.key === 'productId') {
            value = item.item?.product?.productId || item.item?.product?._id || item.item?.product || '—';
        }

        if (col.title === 'Variant' || col.key === 'variant') {
            const options = item.item?.product?.options || [];
            if (options.length > 0) {
                value = options.map(opt => `${opt.name}: ${opt.variants?.map(v => v.value).join(', ')}`).join(' | ');
            } else {
                value = 'No variant';
            }
        }

        if (col.title === 'Qty' || col.key === 'quantity') {
            value = item.item?.quantity || 0;
        }

        if (col.title === 'Unit Price' || col.key === 'unitPrice') {
            const variant = item.item?.product?.options?.[0]?.variants?.[0];
            const mrp = Number(variant?.mrp ?? item.item?.mrp ?? 0);
            value = `₹${mrp.toLocaleString("en-IN")}`;
        }

        if (col.title === 'Discount' || col.key === 'discount') {
            const variant = item.item?.product?.options?.[0]?.variants?.[0];
            const mrp = Number(variant?.mrp ?? item.item?.mrp ?? 0);
            const price = Number(variant?.price ?? item.item?.price ?? item.item?.unitPrice ?? 0);
            const discountAmount = Math.max(mrp - price, 0);
            value = `₹${discountAmount.toLocaleString("en-IN")}`;
        }

        if (col.title === 'Delivery Fee' || col.key === 'deliveryFee') {
            const fee = item.itemIndex === 0 ? Number(item.deliveryCharge || 0) : 0;
            value = `₹${fee.toLocaleString("en-IN")}`;
        }

        if (col.title === 'Total Amount' || col.key === 'totalAmount') {
            const variant = item.item?.product?.options?.[0]?.variants?.[0];
            const price = Number(variant?.price ?? item.item?.price ?? item.item?.unitPrice ?? 0);
            const fee = item.itemIndex === 0 ? Number(item.deliveryCharge || 0) : 0;
            const qty = item.item?.quantity || 1;
            const total = (price * qty) + fee;
            value = `₹${total.toLocaleString("en-IN")}`;
        }

        if (col.title?.trim() === 'Order Status' || col.key === 'status') {
            const status = item.status || 'PENDING';
            value = status.replace(/_/g, ' ').toUpperCase();
        }

        // --- 1. Delivery ID / Ref ---
        if (col.title === 'Delivery ID' || col.key === 'deliveryId') {
            value = item.id || item.deliveryId || 'Manual';
        }

        // --- 2. Delivery Staff Name ---
        if (col.title === 'Delivery Staff Name' || col.key === 'staff') {
            const staff = item.assignedStaffId;
            value = staff ? `${staff.firstName || ''} ${staff.lastName || ''}`.trim() : 'Unassigned';
        }

        // --- 3. Seller Name ---
        if (col.title === 'Seller' || col.title === 'Seller Name' || col.key === 'seller') {
            const seller = item.createdBy || item.seller || item.shopId?.createdBy || item.shop?.createdBy;
            if (seller) {
                const name = seller.name || `${seller.firstName || ''} ${seller.lastName || ''}`.trim();
                value = name ? `${name} (${seller.email || ''})` : (seller.email || '—');
            } else {
                value = 'Unknown';
            }
        }

        // --- 4. Shop Name ---
        if (col.title === 'Shop Name' || col.key === 'shop') {
            value = item.name || item.shopId?.name || item.shop?.name || '—';
        }

        // --- 5. Order ID ---
        if (col.title === 'Order ID' || col.key === 'order' || col.key === 'orderId') {
            value = item.orderId?.orderId || item.orderId || item.orderNo || '—';
            if (typeof value === 'string' && value && !value.startsWith('#')) {
                value = `#${value}`;
            }
        }

        // --- 6. Delivery Earnings ---
        if (col.title === 'Delivery Earnings' || col.key === 'earnings') {
            const deliveryCharge = item.orderId?.deliveryCharge || 0;
            const extraCharge = item.orderId?.extraDeliveryCharge || 0;
            value = `₹${(deliveryCharge + extraCharge).toFixed(2)}`;
        }

        // --- 7. Delivered Amount ---
        if (col.title === 'Delivered Amount' || col.key === 'deliveredAmount') {
            value = `₹${(item.totalAmount || 0).toFixed(2)}`;
        }

        // --- 8. Admin Commission ---
        if (col.title === 'Admin Commission' || col.key === 'commission') {
            const items = item.items || [];
            const sellerPrice = items.reduce((acc, it) => acc + (it.sellerPrice || it.price || 0) * (it.quantity || 1), 0);
            const sellerEarn = sellerPrice - (item.couponDiscount || 0);
            const commission = (item.totalAmount || 0) - sellerEarn;
            value = `₹${commission.toFixed(2)}`;
        }

        // --- 9. Seller Final Earnings ---
        if (col.title === 'Seller Final Earnings' || col.key === 'sellerEarnings') {
            const items = item.items || [];
            const sellerPrice = items.reduce((acc, it) => acc + (it.sellerPrice || it.price || 0) * (it.quantity || 1), 0);
            const sellerEarn = sellerPrice - (item.couponDiscount || 0);
            value = `₹${sellerEarn.toFixed(2)}`;
        }

        // --- 10. Distance ---
        if (col.title === 'Distance' || col.key === 'distance') {
            const dist = item.assignmentHistory?.[0]?.distanceKm || item.distanceKm || null;
            value = dist ? `${dist.toFixed(1)} km` : '—';
        }

        // --- 11. Delivered Date ---
        if (col.title === 'Delivered Date' || col.key === 'deliveredDate') {
            const dateVal = item.actualDeliveryTime || item.updatedAt;
            value = dateVal ? new Date(dateVal).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        }

        // --- 12. Payout Status ---
        if (col.title === 'Payout Status' || col.key === 'payoutStatus') {
            const status = item.deliveryPayoutStatus || item.sellerPayoutStatus || 'PENDING';
            value = status === 'PAID_OUT' ? 'PAID OUT' : 'PENDING';
        }

        // --- 13. Payout Date ---
        if (col.title === 'Payout Date' || col.key === 'payoutDate') {
            const payoutDateVal = item.deliveryPayoutDate || item.sellerPayoutDate;
            value = payoutDateVal ? new Date(payoutDateVal).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
        }

        // --- 14. Payment Method ---
        if (col.title === 'Payment Method' || col.key === 'paymentMethod') {
            const pm = item.paymentMethod || item.paymentType || item.deliveryPaymentMethod || item.orderId?.paymentMethod || item.orderId?.paymentType || item.orderId?.deliveryPaymentMethod || 'COD';
            const cm = item.paymentCollectionMethod || item.orderId?.paymentCollectionMethod;
            const sm = item.paymentSubMethod || item.orderId?.paymentSubMethod;

            let display = pm;
            if (pm === 'COD' && cm === 'ONLINE') {
                display = 'COD + ONLINE';
            } else if (cm && cm !== pm && cm !== 'PREPAID') {
                display = `${pm} + ${cm}`;
            }

            const creditsUsed = item.creditsUsed || item.orderId?.creditsUsed || 0;
            const codAmount = item.codAmount !== undefined ? item.codAmount : (item.orderId?.codAmount !== undefined ? item.orderId.codAmount : null);
            const payableAmount = item.payableAmount !== undefined ? item.payableAmount : (item.orderId?.payableAmount !== undefined ? item.orderId.payableAmount : null);

            if (creditsUsed > 0) {
                if (codAmount === 0 && payableAmount === 0) {
                    display = 'CREDIT';
                } else {
                    display = `CREDIT + ${display}`;
                }
            }

            if (sm && sm.toUpperCase() !== 'ONLINE' && sm.toUpperCase() !== pm) {
                display += ` (${sm.toUpperCase()})`;
            }
            value = display;
        }

        // --- 15. Transaction ID ---
        if (col.title === 'Transaction ID' || col.key === 'transactionId') {
            value = item.transactionId || item.orderId?.transactionId || '—';
        }

        // 1. Full Name
        if ((col.title === 'Full Name' || col.key === 'fullName' || col.key === 'name') && !value) {
            value = item.fullName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : (item.name || item.firstName));
        }

        // 2. Shop
        if (col.title === 'Shop' || col.dataIndex === 'shopId' || col.key === 'shopName') {
            const shop = item.shopId || item.shop;
            if (shop && typeof shop === 'object') {
                value = shop.name || '—';
            } else if (typeof value === 'string' && value.match(/^[0-9a-fA-F]{24}$/)) {
                value = item.shopName || (item.shop && item.shop.name) || value;
            }
        }

        // 3. Status (Boolean or String formatting)
        if (col.title === 'Status' || col.dataIndex === 'isActive' || col.key === 'status') {
            if (typeof value === 'boolean') {
                value = value ? 'Active' : 'Inactive';
            } else if (typeof value === 'string') {
                value = value.replace(/_/g, ' ').toUpperCase();
            }
        }

        // 4. Blocked (Boolean to Text)
        if (col.title === 'Blocked' || col.dataIndex === 'isBlocked') {
            if (typeof value === 'boolean') {
                value = value ? 'Yes' : 'No';
            }
        }

        // 5. Delivery Availability
        if (col.title === 'Status' && (col.dataIndex === 'isAvailable' || col.key === 'status')) {
            if (typeof value === 'boolean') {
                value = value ? 'Available' : 'Busy';
            }
        }

        // 7. Account Status
        if (col.title === 'Account' || col.dataIndex === 'isActive') {
            if (typeof value === 'boolean') {
                value = value ? 'Active' : 'Inactive';
            }
        }

        // 8. Order ID (Handle object with orderId field)
        if (col.title === 'Order ID' && typeof value === 'object' && value !== null) {
            value = value.orderId || JSON.stringify(value);
        }

        // 9. Location (Extract pickup and drop info)
        if (col.title === 'Location' && !value) {
            const pickup = item.pickupAddress ? `${item.pickupAddress.name || 'Pickup'}, ${item.pickupAddress.city || ''}` : '';
            const drop = item.deliveryAddress ? `${item.deliveryAddress.city || ''}, ${item.deliveryAddress.district || ''}` : '';
            value = (pickup && drop) ? `Pickup: ${pickup} | Drop: ${drop}` : (pickup || drop || 'N/A');
        }

        // 10. Assigned Driver / Staff Member
        if ((col.title === 'Assigned Driver' || col.title === 'Staff Member' || col.key === 'driver' || col.key === 'staff') && !value) {
            const staff = item.assignedDriverId || item.staffId;
            value = staff ? `${staff.firstName || ''} ${staff.lastName || ''}`.trim() : (item.staffName || 'Unassigned');
        }

        // 11. Delivery Ref / Order ID
        if ((col.title === 'Delivery Ref' || col.key === 'delivery') && !value) {
            value = item.deliveryId?.id || 'Manual';
        }

        // 5. Sub Category
        if (col.title === 'Sub Category' && (typeof value === 'string' && value.match(/^[0-9a-fA-F]{24}$/))) {
            value = item.subCategoryName || item.subCategory || (item.subCategoryData && item.subCategoryData.name) || value;
        }

        // 12. Admin column — composite name + email
        if ((col.key === 'admin' || col.title === 'Admin') && !value) {
            const name = item.firstName || item.lastName
                ? `${item.firstName || ''} ${item.lastName || ''}`.trim()
                : null;
            value = name ? `${name} (${item.email || ''})` : (item.email || 'N/A');
        }

        // 13. Permissions array — format as readable list
        if ((col.key === 'permissions' || col.dataIndex === 'permissions') && Array.isArray(value)) {
            if (value.length === 0) {
                value = 'No Permissions';
            } else {
                value = value
                    .map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                    .join(', ');
            }
        }

        // 14. Date fields — format ISO strings to readable date
        if (
            (col.dataIndex === 'createdAt' || col.dataIndex === 'updatedAt' || col.dataIndex === 'date' ||
                col.key === 'createdAt' || col.key === 'date' || col.key === 'joinedAt') &&
            typeof value === 'string' && value.includes('T')
        ) {
            try {
                value = new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            } catch (_) { /* keep raw value */ }
        }

        // --- 15. Shop Feedback / Ratings ---
        if (col.title === 'Feedback' || col.key === 'rating') {
            value = item.totalRatings !== undefined ? `(${item.totalRatings})` : '0';
        }

        // --- 16. Visibility (isLive) ---
        if (col.title === 'Visibility' || col.dataIndex === 'isLive' || col.key === 'isLive') {
            value = (item.isLive !== false) ? 'Show' : 'Hide';
        }

        // --- 17. Product Options / Variants ---
        if (col.title === 'Options' || col.key === 'options') {
            const options = item.options || item.variants || [];
            if (options.length === 0) {
                return 'No variants';
            } else {
                if (isExcel) {
                    const optTextList = [];
                    options.forEach(opt => {
                        if (opt.variants && Array.isArray(opt.variants)) {
                            opt.variants.forEach(v => {
                                const mrp = v.mrp ?? v.price ?? 0;
                                const sellerPrice = v.sellerPrice ?? v.price ?? 0;
                                const buyerPrice = v.price ?? 0;
                                const qty = v.stock ?? v.quantity ?? 0;
                                optTextList.push(`${v.value || 'Variant'} (MRP: ₹${mrp}, Seller: ₹${sellerPrice}, Buyer: ₹${buyerPrice}, Stock: ${qty})`);
                            });
                        } else {
                            const mrp = opt.mrp ?? opt.price ?? 0;
                            const sellerPrice = opt.sellerPrice ?? opt.price ?? 0;
                            const buyerPrice = opt.price ?? 0;
                            const qty = opt.stock ?? opt.quantity ?? 0;
                            optTextList.push(`${opt.value || opt.name || 'Variant'} (MRP: ₹${mrp}, Seller: ₹${sellerPrice}, Buyer: ₹${buyerPrice}, Stock: ${qty})`);
                        }
                    });
                    return optTextList.length > 0 ? optTextList.join(' | ') : 'No variants';
                } else {
                    const optList = [];
                    options.forEach(opt => {
                        if (opt.variants && Array.isArray(opt.variants)) {
                            opt.variants.forEach((v, vIdx) => {
                                const mrp = v.mrp ?? v.price ?? 0;
                                const sellerPrice = v.sellerPrice ?? v.price ?? 0;
                                const buyerPrice = v.price ?? 0;
                                const qty = v.stock ?? v.quantity ?? 0;
                                optList.push(
                                    <div key={`${optList.length}-${vIdx}`} style={{ fontSize: '7pt', lineHeight: '1.3', marginBottom: '4px', textAlign: 'left', borderBottom: '1px dashed #e2e8f0', paddingBottom: '3px' }}>
                                        • <strong>{v.value || 'Variant'}</strong><br />
                                        &nbsp;&nbsp;MRP: ₹{mrp} | Sel: ₹{sellerPrice} | Buy: ₹{buyerPrice} | Stk: {qty}
                                    </div>
                                );
                            });
                        } else {
                            const mrp = opt.mrp ?? opt.price ?? 0;
                            const sellerPrice = opt.sellerPrice ?? opt.price ?? 0;
                            const buyerPrice = opt.price ?? 0;
                            const qty = opt.stock ?? opt.quantity ?? 0;
                            optList.push(
                                <div key={optList.length} style={{ fontSize: '7pt', lineHeight: '1.3', marginBottom: '4px', textAlign: 'left', borderBottom: '1px dashed #e2e8f0', paddingBottom: '3px' }}>
                                    • <strong>{opt.value || opt.name || 'Variant'}</strong><br />
                                    &nbsp;&nbsp;MRP: ₹{mrp} | Sel: ₹{sellerPrice} | Buy: ₹{buyerPrice} | Stk: {qty}
                                </div>
                            );
                        }
                    });
                    return <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>{optList}</div>;
                }
            }
        }

        // Handle generic objects
        if (typeof value === 'object' && value !== null) {
            value = value.name || value.label || value.firstName || value.title || JSON.stringify(value);
        }

        if (value === null || value === undefined || value === "") return 'N/A';
        return value;
    };

    const getColumnWidth = (col) => {
        const title = col.title?.trim().toLowerCase() || '';
        const key = col.key?.toLowerCase() || '';

        // Standardized widths for Return/Refund & Cancellation Audit Report columns
        if (title === 'refund id' || title === 'cancel id' || title === 'return id') {
            return '5.0%';
        } else if (title === 'order id') {
            return '6.0%';
        } else if (title === 'order date') {
            return '6.0%';
        } else if (title === 'refund request date' || title === 'cancel date' || title === 'cancellation date') {
            return '6.0%';
        } else if (title === 'seller id') {
            return '6.0%';
        } else if (title === 'shop' || title === 'shop name' || title === 'seller / shop name' || title === 'seller / shop') {
            return '8.0%';
        } else if (title === 'product name') {
            return '12.0%';
        } else if (title === 'product id') {
            return '6.0%';
        } else if (title === 'category') {
            return '8.0%';
        } else if (title === 'sku') {
            return '6.0%';
        } else if (title === 'variant') {
            return '10.0%';
        } else if (title === 'quantity' || title === 'qty') {
            return '3.5%';
        } else if (title === 'unit price') {
            return '6.0%';
        } else if (title === 'product amount') {
            return '6.0%';
        } else if (title === 'discount / coupon' || title === 'discount') {
            return '5.0%';
        } else if (title === 'delivery fee') {
            return '5.0%';
        } else if (title === 'refund amount' || title === 'total amount') {
            return '7.0%';
        } else if (title === 'refund reason' || title === 'reason') {
            return '6.0%';
        } else if (title === 'refund type' || title === 'type') {
            return '5.0%';
        } else if (title === 'payment method') {
            return '7.0%';
        } else if (title === 'refund method') {
            return '5.0%';
        } else if (title === 'refund transaction' || title === 'transaction id') {
            return '7.0%';
        } else if (title === 'unit cost' || title === 'selling price' || title === 'mrp') {
            return '5.0%';
        } else if (title === 'stock value') {
            return '6.0%';
        } else if (title === 'stock in' || title === 'stock sold' || title === 'returned qty' || title === 'damaged qty') {
            return '4.0%';
        } else if (title === 'available stock' || title === 'opening stock' || title === 'reorder level') {
            return '4.0%';
        } else if (title === 'live status' || title === 'active status') {
            return '6.0%';
        } else if (title === 'stock status' || title === 'approval status') {
            return '6.0%';
        } else if (title === 'refund status' || title === 'status' || title === 'cancellation status') {
            return '6.0%';
        } else if (title === 'approved by') {
            return '6.0%';
        } else if (title === 'remarks') {
            return '7.0%';
        } else if (title === 'customer') {
            return '8.0%';
        }

        // Generic defaults
        if (title.includes('stock') || title.includes('qty')) return '4.0%';
        if (title.includes('status')) return '7.0%';
        if (title.includes('date')) return '7.0%';
        if (title.includes('price') || title.includes('amount') || title.includes('fee')) return '6.0%';

        return null;
    };

    const fetchAllData = async () => {
        if (!onFetchFullData) return null;

        setLoading(true);
        try {
            const data = await onFetchFullData();
            if (!data || data.length === 0) {
                Notify.warning("No Data Found", "There are no records to export or print based on your current filters.");
                return null;
            }
            setFullData(data);
            return data;
        } catch (error) {
            console.error("Error fetching full data:", error);
            message.error("Failed to fetch full data");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const data = await fetchAllData();
        if (data && data.length > 0) {
            setIsPrinting(true);

            setTimeout(() => {
                document.body.classList.add('is-printing-mode');
                window.print();

                setTimeout(() => {
                    document.body.classList.remove('is-printing-mode');
                    setIsPrinting(false);
                    setFullData([]);
                }, 1000);
            }, 500);
        } else if (data) {
            message.warning("No data available to print");
        }
    };

    const handleExportExcel = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const data = await fetchAllData();
        if (!data || data.length === 0) return;

        try {
            const exportData = data.map((item, index) => {
                const row = {
                    "S.No": index + 1
                };
                activeColumns.forEach(col => {
                    row[col.title] = getValue(item, col, true, index);
                });
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            const date = new Date().toISOString().split('T')[0];
            const fullFileName = `report_${fileName}_${date}.xlsx`;

            XLSX.writeFile(workbook, fullFileName);
            message.success(`${pageName} exported to Excel!`);
        } catch (error) {
            console.error("Export error:", error);
            message.error("Failed to export Excel file");
        }
    };

    const renderPrintTable = () => {
        if (!isPrinting || fullData.length === 0) return null;

        const portalContent = (
            <div className="print-only-container">
                <style dangerouslySetInnerHTML={{
                    __html: `
                    @media print {
                        @page {
                            size: A4 ${orientation} !important;
                            margin: 6mm 8mm !important;
                        }
                        body {
                            margin: 0 !important;
                            padding: 0 !important;
                            font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                        }
                        .print-only-container {
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 0 !important;
                            margin: 0 !important;
                            font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                        }
                        .print-table {
                            width: 100% !important;
                            border-collapse: collapse !important;
                            table-layout: fixed !important;
                            border: 1px solid #cbd5e1 !important;
                        }
                        .print-table thead {
                            display: table-header-group !important;
                        }
                        .print-table tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        .print-table th,
                        .print-table td {
                            font-size: ${activeColumns.length > 15 ? '6pt' : (activeColumns.length > 10 ? '7pt' : '8pt')} !important;
                            padding: ${activeColumns.length > 15 ? '3px 4px' : '6px 8px'} !important;
                            word-break: break-word !important;
                            white-space: normal !important;
                            border: 1px solid #cbd5e1 !important;
                            vertical-align: top !important;
                            line-height: 1.2 !important;
                        }
                        .print-table th {
                            background-color: #f1f5f9 !important;
                            font-weight: 700 !important;
                            text-align: left !important;
                        }
                        .print-header {
                            margin-bottom: 15px !important;
                            padding-bottom: 8px !important;
                            font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                        }
                        .print-title {
                            font-size: 16pt !important;
                            margin-bottom: 2px !important;
                            font-weight: 700 !important;
                        }
                    }
                ` }} />
                <div className="print-header">
                    <div className="print-title">{pageName}</div>
                    <div className="print-date">Generated on: {new Date().toLocaleString()}</div>
                    <div className="print-stats">Total Records: {fullData.length}</div>
                </div>
                <table className="print-table">
                    <thead>
                        <tr>
                            <th style={{ width: '2.0%', maxWidth: '2.0%' }}>S.No</th>
                            {activeColumns.map((col, idx) => {
                                const w = getColumnWidth(col);
                                const style = w ? { width: w, maxWidth: w } : {};
                                return (
                                    <th key={idx} style={style}>{col.title}</th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {fullData.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                <td style={{ width: '2.0%', maxWidth: '2.0%' }}>{rowIdx + 1}</td>
                                {activeColumns.map((col, colIdx) => {
                                    const w = getColumnWidth(col);
                                    const style = w ? { width: w, maxWidth: w } : {};
                                    return (
                                        <td key={colIdx} style={style}>{getValue(row, col, false, rowIdx)}</td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

        return createPortal(portalContent, document.body);
    };

    return (
        <>
            <div className="table-actions-container no-print" style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative', zIndex: 10 }}>
                <Tooltip title="Export All to Excel">
                    <Button
                        icon={<FileExcelOutlined style={{ fontSize: '16px' }} />}
                        onClick={handleExportExcel}
                        loading={loading}
                        style={{
                            height: '36px',
                            width: '36px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#166534',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(22, 101, 52, 0.2)'
                        }}
                    />
                </Tooltip>
                <Tooltip title="Print All Records (A4)">
                    <Button
                        icon={<PrinterOutlined style={{ fontSize: '16px' }} />}
                        onClick={handlePrint}
                        loading={loading}
                        style={{
                            height: '36px',
                            width: '36px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#475569',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(71, 85, 105, 0.2)'
                        }}
                    />
                </Tooltip>
            </div>
            {renderPrintTable()}
        </>
    );
};

export default TableActions;
